import { useGoalById, useGoalTasks, useSubGoals } from 'features/goals';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import { useUser } from 'shared/hooks/use-user';
import { Paths } from 'shared/routes/paths';
import { DefaultMetricOptions } from 'shared/types/metric-template';
import { TaskType } from 'shared/types/task-base';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import { useTimeframeTimes } from '../../hooks/use-timeframe-times';
import { CompletionBanner } from '../completion-banner';
import { DateRangeSelect } from '../date-range-select';
import { ProgressCarousel } from '../progress-carousel';

export const ProgressSection: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const goal = useGoalById(id);
  const goalTasks = useGoalTasks(id);
  const subGoals = useSubGoals(id);

  const goalDateRange = useTimeframeTimes(goal);
  const [dateRange, setDateRange] = useState(goalDateRange);

  const hasHabits = useMemo(
    () =>
      goalTasks.some(
        ({ type, archivedAt }) => type === TaskType.Habit && !archivedAt,
      ),
    [goalTasks],
  );

  const progressUrl = useMemo(
    () => replaceUrlParams(Paths.GoalDetailProgress, { id: id! }),
    [id],
  );

  if (
    !goal ||
    ((!goal.metric?.id ||
      goal.metric?.id === DefaultMetricOptions.CompletedTasks) &&
      !hasHabits &&
      !subGoals.length)
  ) {
    return null;
  }

  return (
    <PageSectionBase
      title={t('pages.goal-detail.progress.title')}
      url={progressUrl}
      interactions={
        <DateRangeSelect
          customRange={goalDateRange}
          onChange={setDateRange}
          value={dateRange}
          weekStartsOn={weekStartsOn}
        />
      }
      noPadding
    >
      {!!goal && <CompletionBanner goal={goal} />}
      <ProgressCarousel goal={goal} dateRange={dateRange} />
    </PageSectionBase>
  );
};
